"use client"

import { getGachaPreferredToken } from "@anaconda/core/gacha/evm-chains"
import { getGachaEthPaymentChain } from "@anaconda/core/gacha/evm-eth-payment"
import { Trans } from "@lingui/react/macro"
import {
  Button,
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@rarible_int/anaconda_ui_components"
import { atom, wrap } from "@reatom/core"
import { reatomComponent } from "@reatom/react"
import { useEffect, useMemo } from "react"
import { formatEther, formatUnits } from "viem"
import { GachaDialogContent } from "./gacha-dialog-content"
import { formatGachaEthAmount } from "./gacha-eth-payment-format"
import type { GachaEthPaymentRecoveryError } from "./gacha-eth-payment-tracking"
import type { GachaEthPaymentOffer } from "./gacha-execution"

export const GachaEthPaymentReview = reatomComponent(
  ({
    offer,
    machineName,
    busy,
    progress,
    onConfirm,
    onRefresh,
    onCancel,
  }: {
    offer: GachaEthPaymentOffer | null
    machineName: string
    busy: boolean
    progress: string
    onConfirm: () => void
    onRefresh: () => void
    onCancel: () => void
  }) => {
    const chain = offer ? getGachaEthPaymentChain(offer.quote.chainId) : null
    const token = chain ? getGachaPreferredToken(chain) : null
    const tokenSymbol = token?.symbol ?? ""
    const now = useMemo(
      () => atom(Date.now(), "gacha.ethPaymentReview.now"),
      [],
    )
    useEffect(() => {
      if (!offer) return
      const timer = window.setInterval(
        wrap(() => now.set(Date.now())),
        1000,
      )
      return () => window.clearInterval(timer)
    }, [offer, now])
    const expired = Boolean(
      offer && offer.quote.deadline <= Math.floor(now() / 1000),
    )
    return (
      <Dialog
        open={Boolean(offer)}
        onOpenChange={(open) => {
          if (!open && !busy) onCancel()
        }}
      >
        {offer && chain && token && (
          <GachaDialogContent
            className="max-h-[88dvh] max-w-[440px] overflow-y-auto p-5"
            preventDismiss={busy}
          >
            <DialogHeader className="text-left">
              <DialogTitle className="font-headline text-2xl font-normal">
                <Trans>Review ETH payment</Trans>
              </DialogTitle>
              <DialogDescription>
                {offer.quantity} × {machineName}
              </DialogDescription>
            </DialogHeader>
            <div className="flex items-center gap-2 text-xs text-foreground/60">
              <img src={chain.icon} alt="" className="size-4" /> {chain.name}
            </div>
            <div className="flex flex-col gap-3 rounded-md bg-foreground/[0.03] p-4 text-sm">
              <div className="flex justify-between gap-3">
                <span className="text-foreground/60">
                  <Trans>Pack total</Trans>
                </span>
                <span>
                  {formatUnits(
                    BigInt(offer.quote.requiredAmountBase),
                    token.decimals,
                  )}{" "}
                  {tokenSymbol}
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-foreground/60">
                  <Trans>{tokenSymbol} to receive</Trans>
                </span>
                <span>
                  {formatUnits(
                    BigInt(offer.quote.outputAmountBase),
                    token.decimals,
                  )}{" "}
                  {tokenSymbol}
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-foreground/60">
                  <Trans>Maximum ETH conversion</Trans>
                </span>
                <span
                  className="shrink-0 whitespace-nowrap text-right font-mono text-xs"
                  title={`${formatEther(BigInt(offer.quote.maxInputAmountWei))} ETH`}
                >
                  {formatGachaEthAmount(BigInt(offer.quote.maxInputAmountWei))}{" "}
                  ETH
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-foreground/60">
                  <Trans>Estimated swap gas</Trans>
                </span>
                <span
                  className="shrink-0 whitespace-nowrap text-right font-mono text-xs"
                  title={`${formatEther(BigInt(offer.quote.estimatedGasWei))} ETH`}
                >
                  ≈ {formatGachaEthAmount(BigInt(offer.quote.estimatedGasWei))}{" "}
                  ETH
                </span>
              </div>
            </div>
            <div className="space-y-2 text-xs leading-relaxed text-foreground/60">
              <p>
                <Trans>
                  We’ll swap your ETH to {tokenSymbol} for payment. If the
                  purchase fails, you’ll keep the {tokenSymbol} in your wallet.
                </Trans>
              </p>
              <p>
                <Trans>
                  The conversion delivers exactly the {tokenSymbol} pack total
                  with a 0.5% ETH slippage cap. Unused ETH is refunded.
                </Trans>
              </p>
              <p>
                <Trans>Purchase transactions need additional gas.</Trans>
              </p>
            </div>
            {expired && !busy && (
              <p className="text-xs text-brand">
                <Trans>
                  Quote expired. Refresh and review the new price before
                  confirming.
                </Trans>
              </p>
            )}
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="h-10"
                disabled={busy}
                onClick={onCancel}
              >
                <Trans>Cancel</Trans>
              </Button>
              <Button
                className="h-10 flex-1 whitespace-normal"
                disabled={busy}
                onClick={expired ? onRefresh : onConfirm}
              >
                {busy ? (
                  progress
                ) : expired ? (
                  <Trans>Refresh quote</Trans>
                ) : (
                  <Trans>Swap ETH and Buy</Trans>
                )}
              </Button>
            </div>
          </GachaDialogContent>
        )}
      </Dialog>
    )
  },
  "GachaEthPaymentReview",
)

export const GachaEthPaymentRecoveryDialog = reatomComponent(
  ({
    recovery,
    busy,
    progress,
    onCheck,
    onAcknowledge,
    onCancel,
  }: {
    recovery: GachaEthPaymentRecoveryError | null
    busy: boolean
    progress: string
    onCheck: () => void
    onAcknowledge: () => void
    onCancel: () => void
  }) => {
    const chain = recovery
      ? getGachaEthPaymentChain(recovery.submission.chainId)
      : null
    const tokenSymbol = chain ? getGachaPreferredToken(chain).symbol : ""
    return (
      <Dialog
        open={Boolean(recovery)}
        onOpenChange={(open) => {
          if (!open && !busy) onCancel()
        }}
      >
        {recovery && chain && (
          <GachaDialogContent
            className="max-w-[440px] p-5"
            preventDismiss={busy}
          >
            <DialogHeader className="text-left">
              <DialogTitle className="font-headline text-2xl font-normal">
                <Trans>Check ETH conversion</Trans>
              </DialogTitle>
              <DialogDescription>
                {recovery.status === "pending" ? (
                  <Trans>
                    Your conversion is still pending. Do not convert ETH again.
                  </Trans>
                ) : (
                  <Trans>
                    The wallet did not return a reliable submission result.
                    Check wallet activity before continuing.
                  </Trans>
                )}
              </DialogDescription>
            </DialogHeader>
            <p className="text-sm text-foreground/60">
              <Trans>
                No pack purchase was started. If {tokenSymbol} arrived, select{" "}
                {tokenSymbol} to use that balance instead of converting ETH
                again.
              </Trans>
            </p>
            <a
              className="text-sm text-brand underline"
              href={`${chain?.explorerUrl}/${recovery.submission.hash ? `tx/${recovery.submission.hash}` : `address/${recovery.submission.walletAddress}`}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Trans>View wallet activity</Trans>
            </a>
            <div className="flex flex-col gap-2">
              {recovery.status === "pending" && (
                <Button disabled={busy} onClick={onCheck}>
                  {busy ? progress : <Trans>Check conversion status</Trans>}
                </Button>
              )}
              {recovery.status === "unknown" && (
                <Button disabled={busy} onClick={onAcknowledge}>
                  <Trans>I checked my wallet · use {tokenSymbol}</Trans>
                </Button>
              )}
              <Button variant="outline" disabled={busy} onClick={onCancel}>
                <Trans>Close</Trans>
              </Button>
            </div>
          </GachaDialogContent>
        )}
      </Dialog>
    )
  },
  "GachaEthPaymentRecoveryDialog",
)
